import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  AntageneFileItem,
  AntageneCreateChorologieResponse,
  AntageneFilesResponse,
  AntageneExtraireEspecesResponse,
  AnalyseDepartementsTsvUploadResponse,
  AnalyseCreatePayload,
  AnalyseCreateResponse,
  AntageneUploadService,
  AntageneInputCsvResponse,
  AntageneInputRowPayload
} from '../../../services/antagene-upload.service';
import {
  CartoucheCommandeClientItem,
  CartoucheDepartementItem,
  CartouchesService
} from '../../../services/cartouches.service';

interface AntageneCartoucheLine {
  nom: string;
  checked: boolean;
  commandeClientLabel: string;
  departementLabel: string;
}

interface AntageneFileItemView extends AntageneFileItem {
  cartoucheLines: AntageneCartoucheLine[];
}

interface SelectedFileCartouches {
  fileName: string;
  cartouches: string[];
}

@Component({
  selector: 'app-consulter-antagene',
  imports: [CommonModule, FormsModule],
  templateUrl: './consulter-antagene.component.html',
  styleUrl: './consulter-antagene.component.scss'
})
export class ConsulterAntageneComponent implements OnInit {
  protected files: AntageneFileItemView[] = [];
  protected loading = false;
  protected error = '';
  protected success = '';
  protected debugLogs: string[] = [];
  protected deletingByName: Record<string, boolean> = {};
  protected launchingAntagene = false;
  protected creatingChorologie = false;
  protected downloadingResultZip = false;
  protected croisementMode: 'pln' | 'tb' | 'pln_tb' = 'pln_tb';
  protected codesDepartements = '';
  protected departementsTsvFile: File | null = null;
  protected resultFiles: string[] = [];
  private readonly baseUrl = `${environment.apiBaseUrl}/api/`;
  protected searchFilter = '';
  protected currentPage = 1;
  protected pageSize = 10;

  constructor(
    private readonly antageneUploadService: AntageneUploadService,
    private readonly cartouchesService: CartouchesService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadFiles();
  }

  protected get filteredFiles(): AntageneFileItemView[] {
    const needleRaw = (this.searchFilter ?? '').toLowerCase().trim();
    const needleNormalized = this.normalizeForSearch(this.searchFilter);
    if (!needleRaw) {
      return this.files ?? [];
    }

    return (this.files ?? []).filter((file) => {
      const fileNameRaw = (file.name ?? '').toLowerCase();
      const cartouchesRaw = (file.cartouches ?? '').toLowerCase();
      const fileNameNormalized = this.normalizeForSearch(file.name ?? '');
      const cartouchesNormalized = this.normalizeForSearch(file.cartouches ?? '');
      const cartouchesMetaRaw = file.cartoucheLines
        .map((line: AntageneCartoucheLine) => `${line.departementLabel} ${line.commandeClientLabel}`.trim())
        .join(' ')
        .toLowerCase();
      const cartouchesMetaNormalized = this.normalizeForSearch(
        file.cartoucheLines
          .map((line: AntageneCartoucheLine) => `${line.departementLabel} ${line.commandeClientLabel}`.trim())
          .join(' ')
      );

      return (
        fileNameRaw.includes(needleRaw) ||
        cartouchesRaw.includes(needleRaw) ||
        cartouchesMetaRaw.includes(needleRaw) ||
        fileNameNormalized.includes(needleNormalized) ||
        cartouchesNormalized.includes(needleNormalized) ||
        cartouchesMetaNormalized.includes(needleNormalized)
      );
    });
  }

  protected onSearchFilterChange(): void {
    this.currentPage = 1;
  }

  protected get paginatedFiles(): AntageneFileItemView[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredFiles.slice(start, end);
  }

  protected get totalPages(): number {
    return Math.ceil(this.filteredFiles.length / this.pageSize);
  }

  protected downloadFile(filename: string): void {
    const link = document.createElement('a');
    link.href = `${this.baseUrl}gestion-pipe/antagene/download/${encodeURIComponent(filename)}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  protected downloadAllResultFilesZip(): void {
    const filenames = (this.resultFiles ?? []).filter((value) => !!String(value ?? '').trim());
    if (filenames.length === 0 || this.downloadingResultZip) {
      return;
    }

    this.downloadingResultZip = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    this.antageneUploadService.downloadResultsZip({ filenames }).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, this.buildResultZipFilename());
        this.success = `Téléchargement ZIP lancé (${filenames.length} fichier(s)).`;
        this.downloadingResultZip = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = `Erreur lors du téléchargement ZIP: ${this.getErrorMessage(err)}`;
        this.downloadingResultZip = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected deleteFile(filename: string): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le fichier "${filename}" ?`)) {
      return;
    }

    this.deletingByName[filename] = true;
    this.error = '';
    this.cdr.markForCheck();

    this.antageneUploadService.deleteFile(filename).subscribe({
      next: () => {
        this.success = `Fichier "${filename}" supprimé avec succès.`;
        this.deletingByName[filename] = false;
        this.loadFiles();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
        this.deletingByName[filename] = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected launchPipeAntagene(): void {
    const rows = this.buildSelectedRows();
    const selectedFiles = this.buildSelectedFiles();
    const selectedMode = this.croisementMode;
    this.cdr.detectChanges();

    if (rows.length === 0 || selectedFiles.length === 0) {
      this.error = 'Veuillez cocher au moins une correspondance fichier/cartouche.';
      this.success = '';
      this.cdr.markForCheck();
      return;
    }

    this.launchingAntagene = true;
    this.error = '';
    this.success = '';
    this.resultFiles = [];
    this.cdr.markForCheck();

    this.createAnalyses(selectedFiles, 'antagene').subscribe({
      next: (analysis: AnalyseCreateResponse) => {
        const analysisId = Number(analysis?.id);
        const departementsTsvFile = this.departementsTsvFile;
        const hasDepartementsTsv = departementsTsvFile instanceof File;
        const upload$: Observable<AnalyseDepartementsTsvUploadResponse | null> =
          hasDepartementsTsv && Number.isInteger(analysisId) && analysisId > 0
          ? this.antageneUploadService.uploadAnalyseDepartementsTsv(analysisId, departementsTsvFile)
          : of(null);

        upload$.subscribe({
          next: (tsvRes: AnalyseDepartementsTsvUploadResponse | null) => {
        const analyses = [analysis];
        const extractionAnalyseId = Number.isInteger(analysisId) && analysisId > 0 ? analysisId : undefined;
        this.antageneUploadService.writePipeInputCsv(rows, this.codesDepartements, selectedMode).subscribe({
          next: (csvRes: AntageneInputCsvResponse) => {
            this.antageneUploadService.launchAntageneExtraction(selectedMode, extractionAnalyseId).subscribe({
              next: (extractRes: AntageneExtraireEspecesResponse) => {
                const extractionCount = extractRes.extraction?.species_count ?? 0;
                const crossingCount = extractRes.croisement?.output_species_count ?? 0;
                const inputCsvMode = csvRes.methode_tri ?? 'inconnu';
                const extractionMode = extractRes.written_mode ?? extractRes.mode ?? 'inconnu';
                const tsvMessage = tsvRes
                  ? `${tsvRes.filename} importé (${tsvRes.lines_written} ligne(s)). `
                  : '';
                const excelOutputs = (extractRes.excel_outputs ?? [])
                  .map((value) => this.basename(value))
                  .filter((value) => !!value);

                const cartoucheUpdates = this.buildAnalyseCartoucheUpdates(analyses, excelOutputs);

                if (cartoucheUpdates.length === 0) {
                  this.resultFiles = excelOutputs;
                  this.success = `${tsvMessage}${csvRes.written} ligne(s) écrite(s) dans ${csvRes.filename}. 1 analyse créée pour ${rows.length} couple(s). mode écrit input-csv=${inputCsvMode}, mode écrit extraction=${extractionMode}. ${extractionCount} espèce(s) extraite(s). ${crossingCount} espèce(s) après croisement ${selectedMode.toUpperCase()}.`;
                  this.launchingAntagene = false;
                  this.cdr.detectChanges();
                  this.cdr.markForCheck();
                  return;
                }

                forkJoin(
                  cartoucheUpdates.map((update) =>
                    this.antageneUploadService.updateAnalyseCartoucheResultat(
                      update.analyseId,
                      update.cartoucheId,
                      update.fichierResultat,
                    )
                  )
                ).subscribe({
                  next: () => {
                    this.resultFiles = excelOutputs;
                    this.success = `${tsvMessage}${csvRes.written} ligne(s) écrite(s) dans ${csvRes.filename}. 1 analyse créée pour ${rows.length} couple(s). ${cartoucheUpdates.length} fichier(s) résultat mis à jour dans analyse_cartouche. mode écrit input-csv=${inputCsvMode}, mode écrit extraction=${extractionMode}. ${extractionCount} espèce(s) extraite(s). ${crossingCount} espèce(s) après croisement ${selectedMode.toUpperCase()}.`;
                    this.launchingAntagene = false;
                    this.cdr.detectChanges();
                    this.cdr.markForCheck();
                  },
                  error: (err: HttpErrorResponse) => {
                    this.error = `Extraction Antagène terminée, mais erreur lors de la mise à jour des fichiers résultat: ${this.getErrorMessage(err)}`;
                    this.resultFiles = excelOutputs;
                    this.launchingAntagene = false;
                    this.cdr.detectChanges();
                    this.cdr.markForCheck();
                  }
                });
              },
              error: (err: HttpErrorResponse) => {
                this.error = `CSV écrit, mais erreur lors de l'extraction Antagène: ${this.getErrorMessage(err)}`;
                this.resultFiles = [];
                this.launchingAntagene = false;
                this.cdr.detectChanges();
                this.cdr.markForCheck();
              }
            });
          },
          error: (err: HttpErrorResponse) => {
            this.error = `Erreur Antagène: ${this.getErrorMessage(err)}`;
            this.resultFiles = [];
            this.launchingAntagene = false;
            this.cdr.detectChanges();
            this.cdr.markForCheck();
          }
        });
          },
          error: (err: HttpErrorResponse) => {
            this.error = `Erreur lors de l'import du fichier TSV: ${this.getErrorMessage(err)}`;
            this.resultFiles = [];
            this.launchingAntagene = false;
            this.cdr.detectChanges();
            this.cdr.markForCheck();
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.error = `Erreur lors de la création des analyses: ${this.getErrorMessage(err)}`;
        this.resultFiles = [];
        this.launchingAntagene = false;
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      }
    });
  }

  protected setCroisementMode(mode: 'pln' | 'tb' | 'pln_tb'): void {
    this.croisementMode = mode;
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  protected onDepartementsTsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    this.departementsTsvFile = file;
  }

  protected createChorologieTxt(): void {
    this.creatingChorologie = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    this.antageneUploadService.createChorologieFiles().subscribe({
      next: (res: AntageneCreateChorologieResponse) => {
        const steps = res.steps ?? [];
        const outputs = steps
          .map((step) => step.output)
          .filter((value): value is string => !!value);

        this.success = outputs.length > 0
          ? `Fichiers chorologie créés: ${outputs.join(', ')}`
          : 'Fichiers chorologie créés avec succès.';
        this.creatingChorologie = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = `Erreur lors de la création de chorologie.txt: ${this.getErrorMessage(err)}`;
        this.creatingChorologie = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected areAllCartouchesChecked(file: AntageneFileItemView): boolean {
    const lines = file?.cartoucheLines ?? [];
    return lines.length > 0 && lines.every((line) => line.checked);
  }

  protected toggleAllCartouchesForFile(file: AntageneFileItemView, checked: boolean): void {
    const lines = file?.cartoucheLines ?? [];
    for (const line of lines) {
      line.checked = checked;
    }
  }

  private buildSelectedRows(): AntageneInputRowPayload[] {
    const rows: AntageneInputRowPayload[] = [];
    for (const file of this.files) {
      for (const line of file.cartoucheLines) {
        if (line.checked) {
          rows.push({
            nom_fichier: file.name,
            cartouche: line.nom,
          });
        }
      }
    }
    return rows;
  }

  private buildRowsForFile(fileName: string, cartouches: string[]): AntageneInputRowPayload[] {
    const targetFile = this.files.find((file) => file.name === fileName);
    if (!targetFile) {
      return [];
    }

    const allowed = new Set(cartouches.map((value) => value.trim().toLowerCase()).filter((value) => !!value));
    const rows: AntageneInputRowPayload[] = [];

    for (const line of targetFile.cartoucheLines) {
      const cartoucheName = line.nom.trim();
      if (!line.checked || !cartoucheName) {
        continue;
      }

      if (allowed.size > 0 && !allowed.has(cartoucheName.toLowerCase())) {
        continue;
      }

      rows.push({
        nom_fichier: fileName,
        cartouche: cartoucheName,
      });
    }

    return rows;
  }

  private buildSelectedFiles(): SelectedFileCartouches[] {
    const selectedFiles: SelectedFileCartouches[] = [];

    for (const file of this.files) {
      const uniqueCartouches = new Map<string, string>();

      for (const line of file.cartoucheLines) {
        if (!line.checked) {
          continue;
        }

        const cartoucheName = line.nom.trim();
        if (!cartoucheName) {
          continue;
        }

        const key = cartoucheName.toLowerCase();
        if (!uniqueCartouches.has(key)) {
          uniqueCartouches.set(key, cartoucheName);
        }
      }

      if (uniqueCartouches.size === 0) {
        continue;
      }

      selectedFiles.push({
        fileName: file.name,
        cartouches: Array.from(uniqueCartouches.values())
      });
    }

    return selectedFiles;
  }

  private buildAnalyseCartoucheUpdates(
    analyses: AnalyseCreateResponse[],
    excelOutputs: string[],
  ): Array<{ analyseId: number; cartoucheId: number; fichierResultat: string }> {
    const updates: Array<{ analyseId: number; cartoucheId: number; fichierResultat: string }> = [];
    const remainingOutputs = excelOutputs.map((value) => this.basename(value));

    for (const analysis of analyses) {
      const analyseId = Number(analysis?.id);
      const couples = analysis?.couples ?? [];

      if (!Number.isInteger(analyseId) || analyseId <= 0 || couples.length === 0) {
        continue;
      }

      for (const couple of couples) {
        const fichierDepart = String(couple.fichier_depart ?? '').trim();
        const cartoucheName = String(couple.cartouche ?? '').trim();
        const cartoucheId = Number(couple.cartouche_id);
        if (!fichierDepart || !cartoucheName || !Number.isInteger(cartoucheId) || cartoucheId <= 0) {
          continue;
        }

        const sourceToken = this.toOutputToken(this.fileStem(fichierDepart), 'source');
        const cartoucheToken = this.toOutputToken(cartoucheName, 'cartouche');
        const expectedPrefix = `${sourceToken}__${cartoucheToken}__`.toLowerCase();

        let outputIndex = remainingOutputs.findIndex((value) => value.toLowerCase().startsWith(expectedPrefix));

        if (outputIndex === -1 && remainingOutputs.length > 0) {
          outputIndex = 0;
        }

        if (outputIndex === -1) {
          continue;
        }

        const outputName = remainingOutputs.splice(outputIndex, 1)[0];

        if (!outputName) {
          continue;
        }

        updates.push({
          analyseId,
          cartoucheId,
          fichierResultat: outputName,
        });
      }
    }

    return updates;
  }

  private createAnalyses(selectedFiles: SelectedFileCartouches[], typePipe: 'antagene' | 'consensus') {
    const couples = selectedFiles.flatMap((item) =>
      item.cartouches.map((cartouche) => ({
        fichier_depart: item.fileName,
        cartouche,
      }))
    );

    return this.antageneUploadService.createAnalyses({
      analyses: couples,
      methode_tri: this.croisementMode,
      type_pipe: typePipe,
      departements: this.codesDepartements,
    });
  }

  protected previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  protected nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  private loadFiles(): void {
    this.loading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.antageneUploadService.listFiles().subscribe({
      next: (res: AntageneFilesResponse) => {
        this.files = (res.items ?? []).map((item: AntageneFileItem) => ({
          ...item,
          cartoucheLines: this.parseCartoucheLines(item.cartouches)
        }));
        this.debugLogs = res.debug_logs ?? [];
        this.currentPage = 1;
        this.loading = false;
        this.enrichCartoucheLinesWithCommandes();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.files = [];
        this.debugLogs = [];
        this.loading = false;
        this.error = this.getErrorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  private basename(value: string): string {
    const cleaned = String(value ?? '').trim().replace(/\\/g, '/');
    if (!cleaned) {
      return '';
    }

    const parts = cleaned.split('/');
    return parts[parts.length - 1] ?? cleaned;
  }

  private fileStem(filename: string): string {
    const base = this.basename(filename);
    const dotIndex = base.lastIndexOf('.');
    if (dotIndex <= 0) {
      return base;
    }
    return base.slice(0, dotIndex);
  }

  private toOutputToken(value: string, fallback: string): string {
    const token = String(value ?? '')
      .trim()
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/^[._-]+|[._-]+$/g, '');
    return token || fallback;
  }

  private getChorologyFilename(mode: 'pln' | 'tb' | 'pln_tb'): string {
    if (mode === 'pln') {
      return 'chorologie_pln.txt';
    }

    if (mode === 'tb') {
      return 'chorologie_tb.txt';
    }

    return 'chorologie_tb_pln.txt';
  }

  private parseCartoucheLines(rawCartouches: string | undefined): AntageneCartoucheLine[] {
    if (!rawCartouches || !rawCartouches.trim()) {
      return [];
    }

    const seen = new Set<string>();
    const names = rawCartouches
      .split(/[\n,;]+/)
      .map((value: string) => value.trim())
      .filter((value: string) => !!value)
      .filter((value: string) => {
        const key = value.toLowerCase();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });

    return names.map((nom: string) => ({
      nom,
      checked: false,
      commandeClientLabel: '',
      departementLabel: ''
    }));
  }

  private enrichCartoucheLinesWithCommandes(): void {
    const cartoucheNamesByKey = new Map<string, string>();
    for (const file of this.files) {
      for (const line of file.cartoucheLines) {
        const key = line.nom.trim().toLowerCase();
        if (key && !cartoucheNamesByKey.has(key)) {
          cartoucheNamesByKey.set(key, line.nom);
        }
      }
    }

    if (cartoucheNamesByKey.size === 0) {
      return;
    }

    const requests = Array.from(cartoucheNamesByKey.entries()).map(([key, name]) =>
      forkJoin({
        commandes: this.cartouchesService.getCommandesByNom(name).pipe(catchError(() => of([] as CartoucheCommandeClientItem[]))),
        departement: this.cartouchesService.getDepartementByNom(name).pipe(
          catchError(() => of({ cartouche: name, departement: '' } as CartoucheDepartementItem))
        )
      }).pipe(
        map((result: { commandes: CartoucheCommandeClientItem[]; departement: CartoucheDepartementItem }) => ({
          key,
          result
        }))
      )
    );

    forkJoin(requests).subscribe((results: Array<{ key: string; result: { commandes: CartoucheCommandeClientItem[]; departement: CartoucheDepartementItem } }>) => {
      const labelsByKey = new Map<string, string>();
      const departementsByKey = new Map<string, string>();

      for (const result of results) {
        const key = result.key;
        const commandes = result.result.commandes;
        const departement = result.result.departement?.departement ?? '';

        if (commandes?.length) {
          const label = commandes
            .map((item: CartoucheCommandeClientItem) => `${item.numero_commande} - ${item.client_identifiant}`)
            .join(' | ');
          labelsByKey.set(key, label);
        } else {
          labelsByKey.set(key, '');
        }

        departementsByKey.set(key, departement);
      }

      for (const file of this.files) {
        for (const line of file.cartoucheLines) {
          const key = line.nom.trim().toLowerCase();
          line.commandeClientLabel = labelsByKey.get(key) ?? '';
          line.departementLabel = departementsByKey.get(key) ?? '';
        }
      }

      this.files = this.files.map((file) => ({
        ...file,
        cartoucheLines: file.cartoucheLines.map((line) => ({ ...line }))
      }));
      this.cdr.detectChanges();
      this.cdr.markForCheck();
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    const backendMessage = this.extractBackendErrorMessage(err.error);
    if (backendMessage) {
      return this.formatBackendErrorMessage(err.status, backendMessage);
    }

    const transportMessage = this.buildTransportErrorMessage(err);
    if (transportMessage) {
      return transportMessage;
    }

    if (err.status && err.statusText) {
      const requestInfo = this.buildRequestInfo(err.url);
      return `HTTP ${err.status} - ${err.statusText}${requestInfo}`;
    }

    return 'Erreur inconnue';
  }

  private extractBackendErrorMessage(payload: unknown): string {
    if (typeof payload === 'string' && payload.trim()) {
      return payload.trim();
    }

    const serialized = this.stringifyErrorPayload(payload);
    if (serialized) {
      return serialized;
    }

    return '';
  }

  private formatBackendErrorMessage(status: number, message: string): string {
    const normalized = message.toLowerCase();

    if (normalized.includes('echec d\'ecriture') || normalized.includes('échec d\'écriture')) {
      return `Erreur backend: input non ecrivable (${status || 500}) - ${message}`;
    }

    if (normalized.includes('mode de croisement invalide')) {
      return `Erreur backend: mode Antagene invalide (${status || 400}) - ${message}`;
    }

    if (normalized.includes('analyse_id invalide')) {
      return `Erreur backend: analyse_id invalide (${status || 400}) - ${message}`;
    }

    if (normalized.includes('erreur lors du croisement')) {
      return `Erreur backend: echec du croisement Antagene (${status || 400}) - ${message}`;
    }

    return `Erreur backend (${status || 'n/a'}): ${message}`;
  }

  private buildTransportErrorMessage(err: HttpErrorResponse): string {
    const requestInfo = this.buildRequestInfo(err.url);

    if (err.status === 504) {
      return `Timeout proxy/backend (HTTP 504): le backend Antagene n'a pas repondu a temps${requestInfo}.`;
    }

    if (err.status === 503) {
      return `Backend indisponible (HTTP 503): service temporairement hors ligne ou en redemarrage${requestInfo}.`;
    }

    if (err.status === 502) {
      return `Backend indisponible (HTTP 502): passerelle/proxy n'arrive pas a joindre le backend${requestInfo}.`;
    }

    if (err.status === 408) {
      return `Timeout requete (HTTP 408): la requete a expire avant la fin du traitement${requestInfo}.`;
    }

    if (err.status !== 0) {
      return '';
    }

    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (isOffline) {
      return `Reseau indisponible: le poste client est hors ligne${requestInfo}.`;
    }

    if (err.error instanceof ProgressEvent) {
      return `Erreur transport (HTTP 0): CORS, backend indisponible, probleme TLS/certificat ou coupure reseau${requestInfo}.`;
    }

    return `Erreur transport (HTTP 0): requete non aboutie vers le backend Antagene${requestInfo}.`;
  }

  private buildRequestInfo(url: string | null): string {
    const trimmed = String(url ?? '').trim();
    if (!trimmed) {
      return '';
    }
    return ` (url: ${trimmed})`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private buildResultZipFilename(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `resultats_antagene_${yyyy}${mm}${dd}.zip`;
  }

  private stringifyErrorPayload(value: unknown): string {
    if (value == null) {
      return '';
    }

    if (value instanceof ProgressEvent || value instanceof Event) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      const parts = value
        .map((item) => this.stringifyErrorPayload(item))
        .filter((item) => !!item);
      return parts.join(' | ');
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const preferredKeys = ['detail', 'message', 'error', 'errors', 'msg', 'reason'];

      for (const key of preferredKeys) {
        const parsed = this.stringifyErrorPayload(record[key]);
        if (parsed) {
          return parsed;
        }
      }

      try {
        return JSON.stringify(record);
      } catch {
        return '';
      }
    }

    return '';
  }

  private getFastaMissingCorrespondences(err: HttpErrorResponse): string[] {
    const candidates: unknown[] = [
      err.error?.detail?.missing,
      err.error?.missing,
      err.error?.detail?.errors,
      err.error?.errors,
    ];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) {
        continue;
      }

      const items = candidate
        .map((item: unknown) => this.stringifyErrorPayload(item))
        .map((item: string) => item.trim())
        .filter((item: string) => !!item);

      if (items.length > 0) {
        return items;
      }
    }

    return [];
  }

  protected formatDateFR(dateString: string | null | undefined): string {
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  private normalizeForSearch(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase()
      .trim();
  }
}
